import {useCallback} from 'react'

import {ONE_MINUTE_REFRESH_INTERVAL} from '../../../constants'
import {OptimizedSchedule, OptimizerRunResponse} from '../../../interfaces/api'
import {selectScheduleItemsFrom, mapCalculatingPlansResponse} from '../../../selectors/optimizer'
import {usePlantConfig} from '../../usePlantConfigData'
import {useStartOfToday} from '../../useStartOfToday'
import {useUrlParam} from '../../useUrlParam'
import {usePlannerQuery} from '../usePlannerQuery'

interface OptimizedSchedulesAndRunsParams {
  isDisabled?: boolean
  throwOnError?: boolean
}

export const useOptimizedSchedulesAndRuns = ({
  isDisabled = false,
  throwOnError = true
}: OptimizedSchedulesAndRunsParams = {}) => {
  const plantCode = useUrlParam('plantCode')

  const {timezone_id: timezoneId, created_at: createdAt} = usePlantConfig()
  const startOfPlan = useStartOfToday({createdAt, timezoneId})

  const selector = useCallback(
    (data: {optimizerRuns: OptimizerRunResponse[]; optimizedSchedules: OptimizedSchedule[]}) => {
      const {optimizerRuns, optimizedSchedules} = data
      return {
        optimizerRuns: mapCalculatingPlansResponse(optimizerRuns),
        // Since in "/optimized" endpoint BE includes also schedules for ± 1 day around the plan range,
        // we need to filter out old schedule items that are no longer relevant.
        optimizedSchedules: selectScheduleItemsFrom(optimizedSchedules, startOfPlan)
      }
    },
    [startOfPlan]
  )

  return usePlannerQuery('optimizerRunsAndSchedules', [{plantCode}], {
    enabled: !isDisabled,
    throwOnError,
    refetchInterval: ONE_MINUTE_REFRESH_INTERVAL,
    select: selector
  })
}
